const DESKTOP_QUERY = '(min-width: 768px)'
const SWIPE_INTENT_PX = 10
const SWIPE_THRESHOLD_PX = 40
const CLICK_GUARD_MS = 400

class RedesignProductCardSlider {
  constructor (el) {
    this.el = el
    this.slides = Array.from(el.querySelectorAll('[data-slide-index]'))
    this.prevBtn = el.querySelector('.redesign-product-card__slide-prev')
    this.nextBtn = el.querySelector('.redesign-product-card__slide-next')
    this.currentIndex = 0
    this.pendingIndex = 0
    this.total = this.slides.length
    this.hasPointerHover = false
    this.touchStartX = 0
    this.touchStartY = 0
    this.touchDeltaX = 0
    this.isHorizontalSwipe = false
    this.swipedAt = 0

    this._onMouseEnter = this._onMouseEnter.bind(this)
    this._onMouseLeave = this._onMouseLeave.bind(this)
    this._onTouchStart = this._onTouchStart.bind(this)
    this._onTouchMove = this._onTouchMove.bind(this)
    this._onTouchEnd = this._onTouchEnd.bind(this)
    this._onClickCapture = this._onClickCapture.bind(this)
    this._onPrevClick = this._onPrevClick.bind(this)
    this._onNextClick = this._onNextClick.bind(this)
  }

  init () {
    if (this.total <= 1) return

    this.hasPointerHover = window.matchMedia(DESKTOP_QUERY).matches

    this.el.classList.add('is-swipeable')
    this.el.addEventListener('touchstart', this._onTouchStart, { passive: true })
    this.el.addEventListener('touchmove', this._onTouchMove, { passive: true })
    this.el.addEventListener('touchend', this._onTouchEnd)
    this.el.addEventListener('touchcancel', this._onTouchEnd)
    this.el.addEventListener('click', this._onClickCapture, true)

    if (!this.hasPointerHover) return

    this.el.addEventListener('mouseenter', this._onMouseEnter)
    this.el.addEventListener('mouseleave', this._onMouseLeave)

    if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrevClick)
    if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNextClick)
  }

  destroy () {
    this.el.classList.remove('is-swipeable', 'is-hovered')
    this.el.removeEventListener('mouseenter', this._onMouseEnter)
    this.el.removeEventListener('mouseleave', this._onMouseLeave)
    this.el.removeEventListener('touchstart', this._onTouchStart)
    this.el.removeEventListener('touchmove', this._onTouchMove)
    this.el.removeEventListener('touchend', this._onTouchEnd)
    this.el.removeEventListener('touchcancel', this._onTouchEnd)
    this.el.removeEventListener('click', this._onClickCapture, true)

    if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrevClick)
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNextClick)
  }

  _onMouseEnter () {
    this.el.classList.add('is-hovered')
    this._primeNextSlide()
  }

  _onMouseLeave () {
    this.el.classList.remove('is-hovered')
  }

  _onTouchStart (event) {
    const touch = event.touches[0]

    this.touchStartX = touch.clientX
    this.touchStartY = touch.clientY
    this.touchDeltaX = 0
    this.isHorizontalSwipe = false
    this._primeNextSlide()
  }

  _onTouchMove (event) {
    const touch = event.touches[0]
    const deltaX = touch.clientX - this.touchStartX
    const deltaY = touch.clientY - this.touchStartY

    this.touchDeltaX = deltaX

    if (this.isHorizontalSwipe) return
    if (Math.abs(deltaX) < SWIPE_INTENT_PX || Math.abs(deltaX) <= Math.abs(deltaY)) return

    // Load the target slide while the finger is still moving
    this.isHorizontalSwipe = true
    this._ensureSlideImageLoaded(this.slides[this._getSiblingIndex(deltaX < 0 ? 1 : -1)])
  }

  _onTouchEnd () {
    if (!this.isHorizontalSwipe) return

    this.swipedAt = Date.now()
    this.isHorizontalSwipe = false

    if (Math.abs(this.touchDeltaX) < SWIPE_THRESHOLD_PX) return

    this._goTo(this._getSiblingIndex(this.touchDeltaX < 0 ? 1 : -1))
  }

  // The card is wrapped in a link: swallow the click that follows a swipe
  _onClickCapture (event) {
    if (Date.now() - this.swipedAt > CLICK_GUARD_MS) return

    event.preventDefault()
    event.stopPropagation()
  }

  _onPrevClick (event) {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.blur()
    this._goTo(this._getSiblingIndex(-1))
  }

  _onNextClick (event) {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.blur()
    this._goTo(this._getSiblingIndex(1))
  }

  _getSiblingIndex (step) {
    return (this.currentIndex + step + this.total) % this.total
  }

  _goTo (index) {
    if (index === this.currentIndex && index !== 0) return
    if (!this.slides[index]) return

    const slide = this.slides[index]
    const image = slide.querySelector('.redesign-product-card__image')

    this._ensureSlideImageLoaded(slide)
    this.pendingIndex = index

    // Keep the current slide visible until the target image is ready (no blank frame on slow networks)
    if (image && !image.complete) {
      const commit = () => {
        if (this.pendingIndex === index) this._activate(index)
      }

      image.addEventListener('load', commit, { once: true })
      image.addEventListener('error', commit, { once: true })
      return
    }

    this._activate(index)
  }

  _activate (index) {
    if (index === this.currentIndex) return

    this.slides[this.currentIndex].classList.remove('is-active')
    this.slides[index].classList.add('is-active')
    this.currentIndex = index
    this._primeNextSlide()
  }

  _primeNextSlide () {
    if (this.total <= 1) return
    this._ensureSlideImageLoaded(this.slides[this._getSiblingIndex(1)])
  }

  _ensureSlideImageLoaded (slide) {
    if (!slide) return

    const image = slide.querySelector('.redesign-product-card__image')
    if (!image || image.dataset.imageLoaded === 'true') return

    if (image.dataset.src) {
      image.setAttribute('src', image.dataset.src)
      image.removeAttribute('data-src')
    }
    if (image.dataset.srcset) {
      image.setAttribute('srcset', image.dataset.srcset)
      image.removeAttribute('data-srcset')
    }
    if (image.dataset.sizes) {
      image.setAttribute('sizes', image.dataset.sizes)
      image.removeAttribute('data-sizes')
    }

    // Requested on interaction: must not be deferred by native lazy loading
    image.setAttribute('loading', 'eager')
    image.dataset.imageLoaded = 'true'
  }
}

export function initRedesignCardSliders (container = document) {
  container.querySelectorAll('[data-card-slider]').forEach((el) => {
    if (el._redesignSlider) el._redesignSlider.destroy()

    const slider = new RedesignProductCardSlider(el)
    slider.init()
    el._redesignSlider = slider
  })
}
